// Sprachmemos an einer Notiz (E2, Schritt 3; Mockup: „Einsprechen“ an der
// Notiz → „Aufnahme läuft“ mit Welle → „Sprachmemo 0:08 gespeichert“):
//
//  - `SprachmemoKnopf`: der Knopf in der Notiz-Karte und, solange aufgenommen
//    wird, die Leiste fest unten – „Aufnahme läuft – nicht sperren“, die
//    Zeit, die Welle und „Stopp“ groß. Wird die Seite verlassen, endet die
//    Aufnahme und wird gespeichert. Will das Mikrofon nicht, sagt dieselbe
//    Stelle freundlich, was zu tun ist.
//  - `MemoListe`: die Memos der Notiz – abspielen, Dauer, löschen (mit
//    Rückfrage). Abgespielt wird eine eigene Kopie im Arbeitsspeicher
//    (seit 19.10.2026; warum: ../memo.ts, „Abspielen“). Seit Schritt 4 auch
//    nur zum Abspielen (gesendet: ohne Löschen), und ein Memo, das nicht
//    mehr am Gerät liegt, kommt aus dem Büro (../dateien.ts, `dateiZumAnsehen`).
//  - `NotizFuss`: unter dem Notiz-Feld „Wie diktiere ich?“, darunter links
//    „gespeichert“, rechts „Sprachmemo“ als zweite Wahl neben dem Text.
//
// Seit 22.10.2026 (O59): Der Knopf heißt „Sprachmemo“ statt „Einsprechen“,
// klein darunter „nur Ton, kein Text“ – Manuel hat am iPad beim Kunden alles
// eingesprochen und erwartet, dass daraus Text wird. Text wird es mit dem
// Diktat der Tastatur; wie das geht, sagt „Wie diktiere ich?“.
//
// Die Aufnahme selbst steht in ../memo.ts, das Speichern in ../arbeitskopie.ts.

import { useEffect, useRef, useState } from "react";
import { DiktatHilfe, fehlerMelden, Ikon, toastZeigen } from "@happy-energy/oberflaeche";
import type { Memo } from "../speicher.ts";
import { memoLoeschen, type NotizOrt } from "../arbeitskopie.ts";
import {
  dauerZeigen,
  MEMO_HOECHSTENS_SEKUNDEN,
  memoStarten,
  memoStoppen,
  memoStoppenFuer,
  ortSchluessel,
  useMemoAufnahme,
  wiedergabeTyp,
  type AufnahmeStand,
} from "../memo.ts";
import { dateiZumAnsehen, holenFehlgeschlagen, type DateiStand } from "../dateien.ts";
import { Gespeichert, speichernFehlgeschlagen } from "./gemeinsam.tsx";

/** Was gerade abspielt – es spielt immer nur eines, und nie während einer Aufnahme. */
let spieltGerade: HTMLAudioElement | null = null;

function Welle({ striche = 5 }: { striche?: number }) {
  return (
    <span className="welle" aria-hidden="true">
      {Array.from({ length: striche }, (_, i) => (
        <i key={i} />
      ))}
    </span>
  );
}

/** Die Leiste fest unten, solange aufgenommen (oder gerade gespeichert) wird. */
function AufnahmeLeiste({ stand }: { stand: Extract<AufnahmeStand, { art: "laeuft" | "speichert" }> }) {
  const laeuft = stand.art === "laeuft";
  const rest = laeuft ? MEMO_HOECHSTENS_SEKUNDEN - stand.sekunden : null;
  return (
    <div className={`aufnahme-leiste${laeuft ? "" : " speichert"}`} role="region" aria-label="Sprachmemo-Aufnahme">
      <div className="aufnahme-leiste-innen">
        <div className="aufnahme-oben">
          <span className="aufnahme-punkt" aria-hidden="true" />
          <div className="aufnahme-wort" aria-live="polite">
            <b>{laeuft ? "Aufnahme läuft – nicht sperren" : "Wird gespeichert …"}</b>
            <span>
              {!laeuft
                ? "Einen Moment."
                : stand.wach
                  ? "Der Bildschirm bleibt an, bis du stoppst."
                  : "Bitte das Handy nicht sperren – sonst fehlt, was du in der Zeit sagst."}
            </span>
          </div>
          {laeuft && <span className="aufnahme-zeit">{dauerZeigen(stand.sekunden)}</span>}
        </div>
        <Welle striche={21} />
        {laeuft && stand.unterbrochen && (
          <div className="aufnahme-hinweis">Die Aufnahme war unterbrochen – was in der Zeit gesagt wurde, fehlt im Memo.</div>
        )}
        {rest !== null && rest <= 60 && (
          <div className="aufnahme-hinweis">Noch {rest} Sekunden, dann stoppt die Aufnahme von selbst. Für mehr einfach ein weiteres Memo.</div>
        )}
        <button type="button" className="knopf-stopp" onClick={() => memoStoppen()} disabled={!laeuft}>
          <Ikon name="stopp" /> Stopp
        </button>
      </div>
    </div>
  );
}

/** Will das Mikrofon nicht: an derselben Stelle, was zu tun ist. */
function MikrofonHinweis({ text, schliessen }: { text: string; schliessen: () => void }) {
  return (
    <div className="aufnahme-leiste mikro-hinweis" role="alertdialog" aria-labelledby="mikro-hinweis-titel">
      <div className="aufnahme-leiste-innen">
        <div className="aufnahme-oben">
          <Ikon name="achtung" />
          <div className="aufnahme-wort">
            <b id="mikro-hinweis-titel">Sprachmemo geht gerade nicht</b>
            <span>{text}</span>
          </div>
        </div>
        <button type="button" className="knopf-haupt knopf-block" onClick={schliessen}>
          Verstanden
        </button>
      </div>
    </div>
  );
}

/**
 * „Sprachmemo“ an einer Notiz – darunter klein „nur Ton, kein Text“.
 * `vorher`: was vor der Aufnahme noch gespeichert werden soll (der getippte
 * Text). Es läuft immer nur eine Aufnahme – an anderen Notizen ist der Knopf
 * solange gesperrt.
 */
export function SprachmemoKnopf({ ort, vorher }: { ort: NotizOrt; vorher?: () => void }) {
  const stand = useMemoAufnahme();
  const schluessel = ortSchluessel(ort);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const hier = stand.art !== "aus" && stand.ort === schluessel;
  const woanders = stand.art !== "aus" && !hier;

  // Seite verlassen (Zurück, anderes Foto): Die Aufnahme dieser Notiz endet und wird gespeichert.
  useEffect(() => () => memoStoppenFuer(schluessel), [schluessel]);

  async function starten() {
    vorher?.();
    spieltGerade?.pause();
    setHinweis(null);
    const meldung = await memoStarten(ort);
    if (meldung) setHinweis(meldung);
  }

  return (
    <>
      {hier && stand.art === "laeuft" ? (
        <button type="button" className="mikro laeuft" onClick={() => memoStoppen()} aria-label="Aufnahme stoppen">
          <span className="mikro-punkt">
            <Ikon name="mikro" groesse="k" />
          </span>
          <Welle /> {dauerZeigen(stand.sekunden)}
        </button>
      ) : (
        <button
          type="button"
          className="mikro"
          disabled={woanders || hier}
          onClick={() => void starten()}
          aria-label={hier ? undefined : "Sprachmemo aufnehmen"}
        >
          <span className="mikro-punkt">
            <Ikon name="mikro" groesse="k" />
          </span>
          {hier ? (
            stand.art === "startet" ? "Mikrofon …" : "Speichert …"
          ) : (
            <span className="mikro-wort">
              Sprachmemo
              <small>nur Ton, kein Text</small>
            </span>
          )}
        </button>
      )}
      {hier && (stand.art === "laeuft" || stand.art === "speichert") && <AufnahmeLeiste stand={stand} />}
      {hinweis && <MikrofonHinweis text={hinweis} schliessen={() => setHinweis(null)} />}
    </>
  );
}

/**
 * Die Adresse (blob:…) zum Abspielen eines Memos – aus einem frischen Blob
 * im Arbeitsspeicher mit dem Grund-Typ (../memo.ts, „Abspielen“), nie aus
 * einem Blob der Datenbank. Sie entsteht gleich, wenn die Zeile erscheint:
 * Am iPhone muss das Abspielen direkt aus dem Tippen starten – ein Warten
 * auf den Speicher dazwischen ließe es scheitern. Liegt das Memo nicht mehr
 * am Gerät (gesendet), kommt es aus dem Büro; ohne Empfang noch einmal,
 * sobald das Netz zurück ist. `neu` baut sie noch einmal.
 */
function useTonAdresse(memo: Memo & { pruefsumme: string }): { stand: DateiStand; neu: () => void } {
  const [runde, setRunde] = useState(0);
  const [ergebnis, setErgebnis] = useState<{ stand: DateiStand; runde: number; summe: string } | null>(null);
  const summe = memo.pruefsumme;
  const typ = wiedergabeTyp(memo.format, memo.datei?.type);
  useEffect(() => {
    let gilt = true;
    let url: string | null = null;
    const melden = (stand: DateiStand) => {
      if (gilt) setErgebnis({ stand, runde, summe });
    };
    dateiZumAnsehen(summe, typ, () => melden({ art: "holt" })).then(
      (kopie) => {
        if (!gilt) return;
        if (!kopie) return melden({ art: "fehlt" });
        url = URL.createObjectURL(kopie);
        melden({ art: "da", url });
      },
      (fehler: unknown) => {
        const stand = holenFehlgeschlagen(fehler);
        if (stand.art === "fehlt") fehlerMelden("doku", fehler);
        melden(stand);
      },
    );
    return () => {
      gilt = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [summe, typ, runde]);
  const aktuell = ergebnis?.runde === runde && ergebnis.summe === summe ? ergebnis.stand : ({ art: "laedt" } as const);
  // Ohne Empfang: sobald das Gerät wieder Netz meldet, noch einmal holen.
  const ohneNetz = aktuell.art === "ohne-netz";
  useEffect(() => {
    if (!ohneNetz) return;
    const nochmal = () => setRunde((r) => r + 1);
    window.addEventListener("online", nochmal);
    return () => window.removeEventListener("online", nochmal);
  }, [ohneNetz]);
  return { stand: aktuell, neu: () => setRunde((r) => r + 1) };
}

/** Unter einem Memo, das (noch) nicht abspielbar ist: warum – nie nur ein gesperrter Knopf. */
const TON_HINWEIS: Partial<Record<DateiStand["art"], string>> = {
  holt: "Wird aus dem Büro geholt …",
  "ohne-netz": "Ohne Empfang – das Memo liegt sicher im Büro.",
  fehlt: "Am Gerät nicht lesbar.",
};

function MemoZeile({ memo, nr, nurLesen }: { memo: Memo & { pruefsumme: string }; nr: number; nurLesen: boolean }) {
  const adresse = useTonAdresse(memo);
  const url = adresse.stand.art === "da" ? adresse.stand.url : null;
  const tonHinweis = TON_HINWEIS[adresse.stand.art];
  const aufnahme = useMemoAufnahme();
  const ton = useRef<HTMLAudioElement>(null);
  const [spielt, setSpielt] = useState(false);
  const [zeit, setZeit] = useState(0);
  const [rueckfrage, setRueckfrage] = useState(false);
  const [stoerung, setStoerung] = useState(false);
  // Wie oft nach einem Fehler schon eine frische Kopie gebaut wurde – einmal genügt, sonst liefe es im Kreis.
  const neuGebaut = useRef(0);

  /** Das Abspielen ist wirklich gescheitert: melden (ohne Inhalt), sagen, und einmal eine frische Kopie für den nächsten Versuch. */
  function nichtAbspielbar(grund: string) {
    fehlerMelden("doku", new Error(`Sprachmemo lässt sich nicht abspielen: ${grund} (${memo.format}, ${memo.dauerSekunden} s)`));
    setSpielt(false);
    setStoerung(true);
    if (neuGebaut.current < 1) {
      neuGebaut.current += 1;
      adresse.neu();
    }
  }

  function umschalten() {
    const a = ton.current;
    if (!a) return;
    if (!a.paused) {
      a.pause();
      return;
    }
    if (spieltGerade && spieltGerade !== a) spieltGerade.pause();
    spieltGerade = a;
    a.play().catch((fehler: unknown) => {
      const name = fehler instanceof Error ? fehler.name : "";
      // Angehalten, bevor es losging (anderes Memo, Aufnahme) – kein Fehler.
      if (name === "AbortError") return;
      // Das Gerät wollte das Tippen nicht als Erlaubnis nehmen: einfach noch einmal.
      if (name === "NotAllowedError") {
        toastZeigen("Abspielen ging gerade nicht – bitte noch einmal tippen.", "fehler");
        return;
      }
      // Den Ton selbst konnte das Gerät nicht laden: Das hat schon das Ereignis „error“ gemeldet.
      if (a.error) return;
      nichtAbspielbar(name || String(fehler));
    });
  }

  async function loeschen() {
    ton.current?.pause();
    try {
      await memoLoeschen(memo);
      toastZeigen("Sprachmemo gelöscht");
    } catch (fehler) {
      speichernFehlgeschlagen(fehler, "Konnte das Memo nicht löschen – bitte noch einmal.");
    }
  }

  if (rueckfrage) {
    return (
      <li className="memo-zeile memo-rueckfrage" role="alertdialog" aria-labelledby={`memo-weg-${memo.id}`}>
        <span className="memo-wort">
          <b id={`memo-weg-${memo.id}`}>Sprachmemo {nr} löschen?</b>
          Das lässt sich nicht rückgängig machen.
        </span>
        <div className="knopf-reihe">
          <button type="button" className="knopf-rand" onClick={() => setRueckfrage(false)}>
            Behalten
          </button>
          <button type="button" className="knopf-loeschen" onClick={() => void loeschen()}>
            <Ikon name="muell" /> Löschen
          </button>
        </div>
      </li>
    );
  }

  const laeuftGerade = spielt || zeit > 0;
  const anteil = memo.dauerSekunden > 0 ? Math.min(1, zeit / memo.dauerSekunden) : 0;
  const uhrzeit = new Date(memo.aufgenommenAm).toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" });
  return (
    <li className="memo-zeile">
      <button
        type="button"
        className="memo-knopf spielen"
        onClick={umschalten}
        disabled={!url || aufnahme.art !== "aus"}
        aria-label={spielt ? `Sprachmemo ${nr} anhalten` : `Sprachmemo ${nr} abspielen`}
      >
        <Ikon name={spielt ? "pause" : "abspielen"} />
      </button>
      <span className="memo-wort">
        <b>Sprachmemo {nr}</b>
        {laeuftGerade
          ? `${dauerZeigen(zeit)} von ${dauerZeigen(memo.dauerSekunden)}`
          : `${dauerZeigen(memo.dauerSekunden)} · ${uhrzeit} Uhr`}
        {stoerung && !laeuftGerade && (
          <span className="memo-stoerung">
            {nurLesen
              ? "Ließ sich gerade nicht abspielen – im Büro ist es sicher."
              : "Ließ sich gerade nicht abspielen – gespeichert ist es trotzdem und geht ans Büro."}
          </span>
        )}
        {!stoerung && tonHinweis && (
          <span className={adresse.stand.art === "holt" ? "memo-hinweis" : "memo-stoerung"}>{tonHinweis}</span>
        )}
        {laeuftGerade && (
          <span className="memo-balken">
            <i style={{ width: `${anteil * 100}%` }} />
          </span>
        )}
      </span>
      {!nurLesen && (
        <button type="button" className="memo-knopf weg" onClick={() => setRueckfrage(true)} aria-label={`Sprachmemo ${nr} löschen`}>
          <Ikon name="muell" />
        </button>
      )}
      {url && (
        <audio
          ref={ton}
          src={url}
          preload="metadata"
          playsInline
          onError={(e) => nichtAbspielbar(`MediaError ${e.currentTarget.error?.code ?? "?"}`)}
          onPlay={() => {
            setSpielt(true);
            setStoerung(false);
            neuGebaut.current = 0;
          }}
          onPause={() => setSpielt(false)}
          onEnded={() => {
            setSpielt(false);
            setZeit(0);
          }}
          onTimeUpdate={(e) => setZeit(e.currentTarget.currentTime)}
        />
      )}
    </li>
  );
}

/** Die fertigen Memos einer Notiz, in der Reihenfolge der Aufnahme. `nurLesen` (gesendet): abspielen, nicht löschen. */
export function MemoListe({ memos, nurLesen = false }: { memos: Memo[]; nurLesen?: boolean }) {
  const fertig = memos.filter((m): m is Memo & { pruefsumme: string } => m.pruefsumme !== null);
  if (fertig.length === 0) return null;
  return (
    <ul className="memo-liste" aria-label="Sprachmemos">
      {fertig.map((m, i) => (
        <MemoZeile key={m.id} memo={m} nr={i + 1} nurLesen={nurLesen} />
      ))}
    </ul>
  );
}

/**
 * Unter dem Notiz-Feld (Station, Nachtrag und Foto): „Wie diktiere ich?“ –
 * Text liest das Büro am schnellsten (Gernot, 19.10.2026). Darunter links
 * „gespeichert“, rechts „Sprachmemo“ als zweite Wahl neben dem Text. Kein
 * Erklär-Satz mehr (Gernot, 22.10.2026: „nicht mit 100.000 Hinweisen“).
 */
export function NotizFuss({ ort, vorher, gespeichert }: { ort: NotizOrt; vorher: () => void; gespeichert: boolean }) {
  return (
    <>
      <DiktatHilfe />
      <div className="text-karte-unten">
        <span className="text-karte-wort">
          <Gespeichert an={gespeichert} />
        </span>
        <SprachmemoKnopf ort={ort} vorher={vorher} />
      </div>
    </>
  );
}
